/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable max-len */
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { IMaskInput } from 'react-imask';
import styled from 'styled-components';
import merge from 'lodash.merge';

import { mergeStyleWithState } from '../../utils/mergeStyle';
import { history } from '../../configureStore';
import ContainerSkeleton from '../skeleton/ContainerSkeleton';
import { selectVariableData, selectLocalVariableData } from '../../store/page/selectors';
import { formValuesRequest, submitFormRequest, mergeStyleRequest, pageRequest, updateVariableSuccess, actionRequest } from '../../store/page/actions';
import { combineClassNames, replacePlaceholdersWithVariablesV2, handleOpenUrl, getSizesBasedOnScreen, runFunctionsWithTiming, getNestedValue, isClickInsidePortal } from '../../utils/base';
import { buildInternalFns } from '../../utils/handlerScope';
import { buildVariablesData, isVariablesChunk } from '../../utils/variableChunk';
import {
  TextInput, TextAreaInput, PasswordInput, EmailInput, NumberInput, FileInput, PhoneInput, DateTimePickerInput,
  StyleUpdateChunk, RequestUpdateChunk, FunctionUpdateChunk,
  Breakpoints,
  ActionChunk,
} from '../../types/base';
import DateRangePicker from '../DateRangePicker';
import generateComponentStyles from '../../utils/styleHelper';
import { useInitEntrance } from '../../utils/useInitEntrance';

const validateInputPattern = (value: string, pattern?: string, patternFlag?: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  if (!pattern) return true;
  
  try {
    const regex = patternFlag 
      ? new RegExp(pattern, patternFlag)
      : new RegExp(pattern);
      
    return regex.test(value);
  } catch (e) {
    console.error('Invalid regex pattern or flag:', { pattern, patternFlag, error: e });
    return false; // Fail closed for invalid patterns
  }
};

const StyledInput = styled.input<{
  $shape?: any;
  $baseScreenResolution?: number;
  $skipStates?: boolean;
  $parentDisplay?: string;
  $parentFlexDirection?: string;
}>`
    z-index: 1;
  ${(props) => generateComponentStyles(props.$shape, props.$baseScreenResolution, props.$skipStates, props.$parentDisplay, undefined, props.$parentFlexDirection)}
`;

// trigger

const StyledTextarea = styled.textarea<{
  $shape?: any;
  $baseScreenResolution?: number;
  $skipStates?: boolean;
  $parentDisplay?: string;
  $parentFlexDirection?: string;
}>`
  resize: none;
  z-index: 1;
  ${(props) => generateComponentStyles(props.$shape, props.$baseScreenResolution, props.$skipStates, props.$parentDisplay, undefined, props.$parentFlexDirection)}
`;


interface TextInputType extends TextInput {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  pageSettings?: any;
  parentLoading?: boolean;
  parentStyle?: any;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

interface PasswordInputType extends PasswordInput {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  pageSettings?: any;
  parentLoading?: boolean;
  parentStyle?: any;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

interface NumberInputType extends NumberInput {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  pageSettings?: any;
  parentLoading?: boolean;
  parentStyle?: any;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

interface TextAreaInputType extends TextAreaInput {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  pageSettings?: any;
  parentLoading?: boolean;
  parentStyle?: any;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

const Input: React.FC<TextInputType | PasswordInputType  | NumberInputType> = ({
  id, style: stDraft, placeholder,
  value: valueDraft,
  tooltip,
  events: eventsDraft,
  soc,
  pageSettings,
  dataLake,
  active_state: activeStateDraft,
  regexp,
  settings,
  type,
  nestedTable,
  parentIndex,
  styleChunks,
  skeleton: renderSkeleton2,
  parentLoading,
  parentStyle,
  enableLocalShimmer,
  valueOriginal,
  cache,
}) => {
  const { ref } = settings || {};
  const launchExecutedRef = useRef<undefined | null | string>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const store = useStore();
  const { delay = 500, autocomplete } = settings || {};
  const { style: styleDraft, events, active_state, ref: realRef, value } = dataLake?.data ? replacePlaceholdersWithVariablesV2({ value: valueDraft, ref, style: stDraft, events: eventsDraft, active_state: activeStateDraft }, dataLake.data) : { value: valueDraft, ref: undefined, style: stDraft, events: eventsDraft, active_state: activeStateDraft };
const mergeStylesWithChunks = useCallback((style: any, chunks: any, index: number) => {
  const { indexes, refs } = chunks || {};
  let mergedStyle = style;
  
  // Apply index-based styles first
  if (indexes && indexes[id] && indexes[id][index] && typeof index === 'number') {
    mergedStyle = merge({}, mergedStyle, indexes[id][index]);
  }
  
  // Apply ref-based updates at the end
  if (refs && realRef) {
    const refUpdate = refs[realRef];
    if (refUpdate?.type && refUpdate.type === 'input') {
      mergedStyle = merge({}, mergedStyle, refUpdate?.update);
    }
  }
  
  return mergedStyle;
}, [id, realRef]);

  const renderSceleton = useMemo(() => {
    if (enableLocalShimmer) {
      if (cache === false) {
        return false;
      }
      return (active_state === 'loading' || parentLoading) ? !!((cache || (typeof valueOriginal === 'string' && /{{.*}}/.test(valueOriginal)))) : false;
    }
    return renderSkeleton2;
  }, [renderSkeleton2, active_state, enableLocalShimmer, valueOriginal, parentLoading]);

  // First useMemo to merge active state styles with base style
  const { effectiveState, entranceAnimation } = useInitEntrance(styleDraft, active_state, inputRef);
  const mergedStyleWithState = useMemo(() => {
    let mergedStyle = styleDraft;
    if (effectiveState && styleDraft?.states?.[effectiveState]) {
      mergedStyle = mergeStyleWithState({}, styleDraft, styleDraft.states[effectiveState]);
    }

    // Handle states in breakpoints
    if (styleDraft?.breakpoints) {
      mergedStyle.breakpoints = Object.entries(styleDraft.breakpoints).reduce((acc, [breakpoint, breakpointStyle]) => {
        const typedBreakpointStyle = breakpointStyle as Breakpoints;
        if (typedBreakpointStyle?.states?.[effectiveState]) {
          acc[breakpoint] = mergeStyleWithState({}, typedBreakpointStyle, typedBreakpointStyle.states[effectiveState]);
        } else {
          acc[breakpoint] = typedBreakpointStyle;
        }
        return acc;
      }, {} as typeof styleDraft.breakpoints);
    }

    if (entranceAnimation) {
      mergedStyle = { ...mergedStyle, animation: entranceAnimation };
    }
    return mergedStyle;
  }, [styleDraft, effectiveState, entranceAnimation]);

  // Second useMemo to merge styles with chunks based on parentIndex
  const style = useMemo(() => mergeStylesWithChunks(mergedStyleWithState, styleChunks, parentIndex), [mergedStyleWithState, styleChunks, parentIndex]);
  const baseScreenResolution =  pageSettings?.baseScreenResolution ? getSizesBasedOnScreen(pageSettings?.baseScreenResolution) : undefined;
  const [internalValue, setInternalValue] = useState<string>(value);
  const { autofocus } = style || {};
  const dispatch = useDispatch();

  // TNBEWEB-1992: shared handler scope (adds data.* / _c.* / _config.* / this.* on top of the
  // legacy $get/$set); `thisRow`/`rowPath` come from the repeatable row context (undefined otherwise).
  const internalFns = buildInternalFns({
    store,
    thisRow: dataLake?.data,
    rowPath: dataLake?.rowPath,
    owner: { id, parentIndex, row: dataLake?.data, activeState: active_state },
  });

  // Variables are assigned before every other chunk in the event (first in the
  // stack). Resolve them synchronously so the style/action/function chunks that
  // follow already see the new values. `{{this.*}}` resolves against this
  // component's own data.
  const applyVariablesFirst = (updates: any[]) => {
    const variableUpdates = (updates || []).filter((u) => isVariablesChunk(u));
    if (!variableUpdates.length) return;
    const state: any = store.getState();
    const data = buildVariablesData(variableUpdates as any, {
      variables: selectVariableData(state),
      localVariables: selectLocalVariableData(state),
      thisContext: dataLake?.data,
      formValues: state.page?.formValues,
    });
    if (data && Object.keys(data).length) {
      dispatch(updateVariableSuccess({ data }));
    }
  };

  const handleActionChunk = (actionUpdates: ActionChunk[], filterLoadingState?: StyleUpdateChunk[], onSuccessEvts?: any[], onErrorEvts?: any[]) => {
    actionUpdates.forEach((update) => {
      dispatch(actionRequest({
        actionUpdate: update,
        componentId: id,
        onSuccess: onSuccessEvts,
        onError: onErrorEvts,
      }, filterLoadingState));
    });
  };

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const {
    on_focus: focusStyles,
    on_change: changeStyles,
    on_blur: blurStyles,
    on_launch: launchUpdates,
    on_hover: hoverUpdates,
    on_unhover: unhoverUpdates,
    on_regexp_success: regexpSuccessUpdates,
    on_regexp_error: regexpErrorUpdates,
    on_outclick: outclickUpdates,
    on_success: onSuccessUpdates,
    on_error: onErrorUpdates,
  } = events || {};

  // Repeatable rows are reconciled by index, so this instance can be reused for a
  // DIFFERENT entity (page change, sort, filter). Re-sync on the row identity too --
  // otherwise a swap between two rows whose stored value is identical (e.g. both empty)
  // leaves the previous row's typed text in place.
  const rowIdentity = realRef || id;

  useEffect(() => {
    if (type === 'number' && typeof value === 'string') {
    setInternalValue(value.replace(/,/g, '.'));
  } else {
    setInternalValue(value);
  }
    }, [type, value, rowIdentity]);

  useEffect(() => {
    if (type === 'number' && typeof value === 'string') {
      if (value !== internalValue) {
        setInternalValue(value.replace(/,/g, '.'));
      }
    } else if (value !== internalValue) {
        setInternalValue(value);
      }
  }, [type, value, rowIdentity]);

  // const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
  //   // Если input в фокусе — снимаем фокус, чтобы не менялось значение
  //   if (type === 'number' && document.activeElement === inputRef.current) {
  //     inputRef.current?.blur();
  //     setTimeout(() => {
  //       inputRef.current?.focus();
  //     }, 0);
  //   }
  // };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (active_state === 'disabled' || active_state === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    dispatch(submitFormRequest({ block: true }));

    let newValue = e.target.value;

      // If type is number, allow only digits and one decimal separator
  if (type === 'number') {
    // Replace comma with dot
    newValue = newValue.replace(/,/g, '.');
    // Remove invalid characters (allow digits and one dot)
    newValue = newValue.replace(/[^0-9.]/g, '');
    // Only one dot allowed
    const parts = newValue.split('.');
    if (parts.length > 2) {
      newValue = `${parts[0]  }.${  parts.slice(1).join('')}`;
    }
  }

        // Check max length
    if (regexp?.max_length && newValue.length > regexp.max_length) {
      return;
    }
    setInternalValue(newValue);
    dispatch(formValuesRequest({ field: { id: realRef || id, value: newValue } }));
    // Check pattern validation
    if (changeStyles) {
      const styleUpdates = changeStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = changeStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = changeStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = changeStyles.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(changeStyles);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
          handleActionChunk(actionUpdates, filterLoadingState, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
            const { delay } = config || {};
            if (update?.request && method === 'command') {
              dispatch(pageRequest({
                location: { pathname: value },
                silent,
                componentId: id,
                onSuccess: onSuccessUpdates,
                onError: onErrorUpdates,
              },
            filterLoadingState
            ));
            } else if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }, filterLoadingState));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
    if (regexp?.pattern && typeof regexp.pattern === 'string') {
      const result = validateInputPattern(newValue, regexp.pattern);
      if (regexpErrorUpdates && !result) {
          const styleUpdates = regexpErrorUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
          const requestUpdates = regexpErrorUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
          const functionUpdates = regexpErrorUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
          const actionUpdates = regexpErrorUpdates.filter((update): update is ActionChunk => update && 'action' in update);
          applyVariablesFirst(regexpErrorUpdates);
          const runEffects = () => {
            if (styleUpdates.length) {
              dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
            }
            if (actionUpdates.length) {
              handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
            }
            if (requestUpdates.length) {
              requestUpdates.forEach((update) => {
                const { method, value, silent, config } = update?.request || {};
                const { delay } = config || {};
                if (update?.request && method === 'command') {
                  dispatch(pageRequest({
                    location: { pathname: value },
                    silent,
                    componentId: id,
                    onSuccess: onSuccessUpdates,
                    onError: onErrorUpdates,
                  }));
                } else if (update?.request && method === 'action') {
                  dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
                } else if (update?.request && method === 'open-url') {
                  handleOpenUrl(value, delay, silent, history);
                }
              });
            }
          };
          runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
      } else if (regexpSuccessUpdates && result) {
          const styleUpdates = regexpSuccessUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
          const requestUpdates = regexpSuccessUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
          const functionUpdates = regexpSuccessUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
          const actionUpdates = regexpSuccessUpdates.filter((update): update is ActionChunk => update && 'action' in update);
          applyVariablesFirst(regexpSuccessUpdates);
          const runEffects = () => {
            if (styleUpdates.length) {
              dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
            }
            if (actionUpdates.length) {
              const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
              handleActionChunk(actionUpdates, filterLoadingState, onSuccessUpdates, onErrorUpdates);
            }
            if (requestUpdates.length) {
              requestUpdates.forEach((update) => {
                const { method, value, silent, config } = update?.request || {};
                const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
                const { delay } = config || {};
                if (update?.request && method === 'command') {
                  dispatch(pageRequest({
                    location: { pathname: value },
                    silent,
                    componentId: id,
                    onSuccess: onSuccessUpdates,
                    onError: onErrorUpdates,
                  }, filterLoadingState));
                } else if (update?.request && method === 'action') {
                  dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }, filterLoadingState));
                } else if (update?.request && method === 'open-url') {
                  handleOpenUrl(value, delay, silent, history);
                }
              });
            }
          };
          runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
      }
    }
    if (soc) {
      dispatch(submitFormRequest({ value: 'soc', delay, componentId: id }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (active_state === 'disabled' || active_state === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // TNBEWEB-1989: inputs aren't wrapped in a <form>, so the on-screen keyboard's
    // action button ("Go"/"Next"/"Done") has no submit target — the browser either
    // does nothing or heuristically tabs to an arbitrary field. Define the action
    // explicitly: the action key closes the keyboard by blurring the field. blur()
    // persists the value via handleBlur; preventDefault stops the heuristic tab.
    // Only applies to single-line inputs — <textarea> keeps Enter as newline
    // (TextArea has no handleKeyDown handler).
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current?.blur();
    }
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (active_state === 'disabled' || active_state === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (focusStyles) {
      const styleUpdates = focusStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = focusStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = focusStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = focusStyles.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(focusStyles);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'command') {
              dispatch(pageRequest({
                location: { pathname: value },
                 silent,
                componentId: id,
                onSuccess: onSuccessUpdates,
                onError: onErrorUpdates,
                }));
            } else if (update?.request && method === 'action') {
              dispatch(submitFormRequest(
                { value, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (active_state === 'disabled' || active_state === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    dispatch(submitFormRequest({ block: true }));

    const newValue = e.target.value;

    setInternalValue(newValue);
    dispatch(formValuesRequest({ field: { id: realRef || id, value: newValue } }));
    if (blurStyles) {
      const styleUpdates = blurStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = blurStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = blurStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = blurStyles.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(blurStyles);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'command') {
              dispatch(pageRequest({
                location: { pathname: value },
                silent,
                componentId: id,
                onSuccess: onSuccessUpdates,
                onError: onErrorUpdates,
              }));
            } else if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  };

  const handleHover = (e: React.PointerEvent<HTMLInputElement>) => {
    if (e.pointerType === 'touch') return; // TNBEWEB-1980: finger tap — don't apply hover
    if (hoverUpdates) {
      const styleUpdates = hoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = hoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = hoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = hoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(hoverUpdates);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }

  }

  const handleUnhover = (e: React.PointerEvent<HTMLInputElement>) => {
    if (e.pointerType === 'touch') return; // TNBEWEB-1980: finger tap — don't apply unhover
    if (unhoverUpdates) {
      const styleUpdates = unhoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = unhoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = unhoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = unhoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(unhoverUpdates);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  }

  useEffect(() => {
    if (launchExecutedRef.current !== style?.visibility && launchUpdates && launchUpdates.length) {
      launchExecutedRef.current = style?.visibility; // Set the flag to true
      const requestUpdates = launchUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = launchUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = launchUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(launchUpdates);
      const runEffects = () => {
        if (actionUpdates.length) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              // const newTimeout = setTimeout(() => {
              //   dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
              // }, config?.delay * 1000 || 0);

              // timeoutsRef.current.push(newTimeout);
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects);

    }

    return () => {
      timeoutsRef?.current?.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, [launchUpdates, style?.visibility]);

  // Memoize the filtered updates to avoid recalculating on every click
  const filteredUpdates = useMemo(() => {
    if (!outclickUpdates) return null;
    
    return {
      styleUpdates: outclickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update),
      requestUpdates: outclickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update),
      functionUpdates: outclickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update),
      actionUpdates: outclickUpdates.filter((update): update is ActionChunk => update && 'action' in update)
    };
  }, [outclickUpdates]);

  useEffect(() => {
    if (!outclickUpdates?.length) return undefined;
    if (style?.visibility === 'hidden') return undefined;

    const handleUnclick = (e: MouseEvent) => {
      if (active_state === 'disabled' || active_state === 'loading') return;
      
      if (
        outclickUpdates?.length &&
        inputRef.current && 
        !inputRef.current.contains(e.target as Node) &&
        !isClickInsidePortal(e.target as Node) &&
        filteredUpdates
      ) {
        const runEffects = () => {
          // Apply style updates
          if (filteredUpdates.styleUpdates.length) {
            dispatch(mergeStyleRequest(filteredUpdates.styleUpdates, parentIndex));
          }

          // Process action updates
          if (filteredUpdates.actionUpdates.length) {
            handleActionChunk(filteredUpdates.actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
          }

          // Process request updates
          if (filteredUpdates.requestUpdates.length) {
            filteredUpdates.requestUpdates.forEach((update) => {
              const { method, value, silent, config } = update?.request || {};
              const { delay } = config || {};
              if (update?.request && method === 'action') {
                dispatch(submitFormRequest({
                  value,
                  silent,
                  delay,
                  componentId: id,
                  onSuccess: onSuccessUpdates,
                  onError: onErrorUpdates,
                }));
              } else if (update?.request && method === 'open-url') {
                handleOpenUrl(value, delay, silent, history);
              }
            });
          }
        };
        applyVariablesFirst(outclickUpdates);
        runFunctionsWithTiming(filteredUpdates.functionUpdates, internalFns, runEffects);
      }
    };
    
    document.addEventListener('mousedown', handleUnclick, true);
    return () => {
      document.removeEventListener('mousedown', handleUnclick, true);
    };
  }, [
    parentIndex, 
    active_state, 
    id, 
    stDraft?.states?.loading,
    outclickUpdates?.length,
    style?.visibility,
  ]);

  if (renderSceleton && style) {
    return <ContainerSkeleton
      style={style}
      baseScreenResolution={baseScreenResolution}
      skipStates={active_state === 'disabled' || active_state === 'loading'}
    />;
  }

  const InputEl = StyledInput;

  return (
  regexp?.mask_pattern ? (
    <InputEl
      as={IMaskInput}
      mask={regexp.mask_pattern}
      value={internalValue}
      // onWheel={handleWheel}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onPointerEnter={hoverUpdates?.length ? handleHover : undefined}
      onPointerLeave={unhoverUpdates?.length ? handleUnhover : undefined}
      disabled={active_state === 'disabled'}
      inputRef={inputRef}
      placeholder={placeholder}
       data-id={realRef || id}
      id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
      type={type === 'number' ? 'text' : (type || 'text')}
      style={{ display: style?.visibility === 'hidden' ? 'none' : undefined }}
      autoFocus={autofocus}
      maxLength={regexp?.max_length}
      onClick={(e) => {
        e.stopPropagation();
      }}
      $shape={style}
      autoComplete={autocomplete === false ? 'off' : 'on'}
      $baseScreenResolution={baseScreenResolution}
      $skipStates={active_state === 'disabled' || active_state === 'loading'}
      $parentDisplay={parentStyle?.display}
      $parentFlexDirection={typeof parentStyle?.flex === 'object' ? parentStyle?.flex?.direction : undefined}
      data-component="input"
      className={combineClassNames(style.class)}
      title={tooltip}
      onInput={handleInput}
      // TNBEWEB-1989: static hint — the action button reads "Done", matching the
      // "close the keyboard" behavior in handleKeyDown (dynamic/configurable label is out of scope).
      enterKeyHint="done"
      lang="en"
    />
  ) : (
    <InputEl
      ref={inputRef}
      style={{ display: style?.visibility === 'hidden' ? 'none' : undefined }}
      placeholder={placeholder}
       data-id={realRef || id}
      id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
      type={type === 'number' ? 'text' : (type || 'text')}
      // onWheel={handleWheel}
      $shape={style}
      $baseScreenResolution={baseScreenResolution}
      $skipStates={active_state === 'disabled' || active_state === 'loading'}
      $parentDisplay={parentStyle?.display}
      $parentFlexDirection={typeof parentStyle?.flex === 'object' ? parentStyle?.flex?.direction : undefined}
      disabled={active_state === 'disabled'}
      data-component="input"
      autoComplete={autocomplete === false ? 'off' : 'on'}
      className={combineClassNames(style.class)}
      value={internalValue}
      title={tooltip}
      autoFocus={autofocus}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onClick={(e) => e.stopPropagation()}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onPointerEnter={hoverUpdates?.length ? handleHover : undefined}
      onPointerLeave={unhoverUpdates?.length ? handleUnhover : undefined}
      maxLength={regexp?.max_length}
      // TNBEWEB-1989: static hint — the action button reads "Done", matching the
      // "close the keyboard" behavior in handleKeyDown (dynamic/configurable label is out of scope).
      enterKeyHint="done"
      lang="en"
    />
  )
);
};

const TextArea: React.FC<TextAreaInputType> = ({
  id, style: stDraft, placeholder,
  value: valueDraft,
  tooltip,
  events: eventsDraft,
  soc,
  pageSettings,
  dataLake,
  active_state: activeStateDraft,
  regexp,
  settings,
  nestedTable,
  parentIndex,
  styleChunks,
  skeleton: renderSkeleton2,
  parentLoading,
  parentStyle,
  enableLocalShimmer,
  valueOriginal,
  cache,
}) => {
  const { ref } = settings || {};
  const launchExecutedRef = useRef<undefined | null | string>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { delay = 500 } = settings || {};
  const store = useStore();
  const { style: styleDraft, events, active_state, ref: realRef, value } = dataLake?.data ? replacePlaceholdersWithVariablesV2({ value: valueDraft, ref, style: stDraft, events: eventsDraft, active_state: activeStateDraft}, dataLake.data) : { value: valueDraft, ref: undefined, style: stDraft, events: eventsDraft, active_state: activeStateDraft };
const mergeStylesWithChunks = useCallback((style: any, chunks: any, index: number) => {
  const { indexes, refs } = chunks || {};
  let mergedStyle = style;
  
  // Apply index-based styles first
  if (indexes && indexes[id] && indexes[id][index] && typeof index === 'number') {
    mergedStyle = merge({}, mergedStyle, indexes[id][index]);
  }
  
  // Apply ref-based updates at the end
  if (refs && realRef) {
    const refUpdate = refs[realRef];
    if (refUpdate?.type && refUpdate.type === 'input') {
      mergedStyle = merge({}, mergedStyle, refUpdate?.update);
    }
  }
  
  return mergedStyle;
}, [id, realRef]);

  const renderSceleton = useMemo(() => {
    if (enableLocalShimmer) {
      if (cache === false) {
        return false;
      }
      return (active_state === 'loading' || parentLoading) ? !!((cache || (typeof valueOriginal === 'string' && /{{.*}}/.test(valueOriginal)))) : false;
    }
    return renderSkeleton2;
  }, [renderSkeleton2, active_state, enableLocalShimmer, valueOriginal, parentLoading]);

  // First useMemo to merge active state styles with base style
  const { effectiveState, entranceAnimation } = useInitEntrance(styleDraft, active_state, inputRef);
  const mergedStyleWithState = useMemo(() => {
    let mergedStyle = styleDraft;
    if (effectiveState && styleDraft?.states?.[effectiveState]) {
      mergedStyle = mergeStyleWithState({}, styleDraft, styleDraft.states[effectiveState]);
    }

    // Handle states in breakpoints
    if (styleDraft?.breakpoints) {
      mergedStyle.breakpoints = Object.entries(styleDraft.breakpoints).reduce((acc, [breakpoint, breakpointStyle]) => {
        const typedBreakpointStyle = breakpointStyle as Breakpoints;
        if (typedBreakpointStyle?.states?.[effectiveState]) {
          acc[breakpoint] = mergeStyleWithState({}, typedBreakpointStyle, typedBreakpointStyle.states[effectiveState]);
        } else {
          acc[breakpoint] = typedBreakpointStyle;
        }
        return acc;
      }, {} as typeof styleDraft.breakpoints);
    }

    if (entranceAnimation) {
      mergedStyle = { ...mergedStyle, animation: entranceAnimation };
    }
    return mergedStyle;
  }, [styleDraft, effectiveState, entranceAnimation]);

  // Second useMemo to merge styles with chunks based on parentIndex
  const style = useMemo(() => mergeStylesWithChunks(mergedStyleWithState, styleChunks, parentIndex), [mergedStyleWithState, styleChunks, parentIndex]);
  const baseScreenResolution =  pageSettings?.baseScreenResolution ? getSizesBasedOnScreen(pageSettings?.baseScreenResolution) : undefined;
  const [internalValue, setInternalValue] = useState<string>(value);
  const { autofocus } = style || {};
  const dispatch = useDispatch();

  // TNBEWEB-1992: shared handler scope (adds data.* / _c.* / _config.* / this.* on top of the
  // legacy $get/$set); `thisRow`/`rowPath` come from the repeatable row context (undefined otherwise).
  const internalFns = buildInternalFns({
    store,
    thisRow: dataLake?.data,
    rowPath: dataLake?.rowPath,
    owner: { id, parentIndex, row: dataLake?.data, activeState: active_state },
  });

  // Variables are assigned before every other chunk in the event (first in the
  // stack). Resolve them synchronously so the style/action/function chunks that
  // follow already see the new values. `{{this.*}}` resolves against this
  // component's own data.
  const applyVariablesFirst = (updates: any[]) => {
    const variableUpdates = (updates || []).filter((u) => isVariablesChunk(u));
    if (!variableUpdates.length) return;
    const state: any = store.getState();
    const data = buildVariablesData(variableUpdates as any, {
      variables: selectVariableData(state),
      localVariables: selectLocalVariableData(state),
      thisContext: dataLake?.data,
      formValues: state.page?.formValues,
    });
    if (data && Object.keys(data).length) {
      dispatch(updateVariableSuccess({ data }));
    }
  };

  const handleActionChunk = (actionUpdates: ActionChunk[], filterLoadingState?: StyleUpdateChunk[], onSuccessEvts?: any[], onErrorEvts?: any[]) => {
    actionUpdates.forEach((update) => {
      dispatch(actionRequest({
        actionUpdate: update,
        componentId: id,
        onSuccess: onSuccessEvts,
        onError: onErrorEvts,
      }, filterLoadingState));
    });
  };

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const {
    on_focus: focusStyles,
    on_change: changeStyles,
    on_blur: blurStyles,
    on_launch: launchUpdates,
    on_hover: hoverUpdates,
    on_unhover: unhoverUpdates,
    on_regexp_success: regexpSuccessUpdates,
    on_regexp_error: regexpErrorUpdates,
    on_outclick: outclickUpdates,
    on_success: onSuccessUpdates,
    on_error: onErrorUpdates,
  } = events || {};

  // See the note in Input: re-sync on row identity so a reused instance cannot keep
  // the previous entity's typed text when both rows share the same stored value.
  const rowIdentity = realRef || id;

  useEffect(() => {
    setInternalValue(value);
  }, [value, rowIdentity]);

  useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(value);
    }
  }, [value, rowIdentity]);

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (active_state === 'disabled' || active_state === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    dispatch(submitFormRequest({ block: true }));

    const newValue = e.target.value;

        // Check max length
    if (regexp?.max_length && newValue.length > regexp.max_length) {
      return;
    }
    setInternalValue(newValue);
    dispatch(formValuesRequest({ field: { id: realRef || id, value: newValue } }));
    // Check pattern validation
    if (changeStyles) {
      const styleUpdates = changeStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = changeStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = changeStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = changeStyles.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(changeStyles);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
          handleActionChunk(actionUpdates, filterLoadingState, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
            const { delay } = config || {};
            if (update?.request && method === 'command') {
              dispatch(pageRequest({
                location: { pathname: value },
                silent,
                componentId: id,
                onSuccess: onSuccessUpdates,
                onError: onErrorUpdates,
              }, filterLoadingState));
            } else if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }, filterLoadingState));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
    if (regexp?.pattern && typeof regexp.pattern === 'string') {
      const result = validateInputPattern(newValue, regexp.pattern, 'gs');
      if (regexpErrorUpdates && !result) {
          const styleUpdates = regexpErrorUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
          const requestUpdates = regexpErrorUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
          const functionUpdates = regexpErrorUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
          const actionUpdates = regexpErrorUpdates.filter((update): update is ActionChunk => update && 'action' in update);
          applyVariablesFirst(regexpErrorUpdates);
          const runEffects = () => {
            if (styleUpdates.length) {
              dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
            }
            if (actionUpdates.length) {
              handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
            }
            if (requestUpdates.length) {
              requestUpdates.forEach((update) => {
                const { method, value, silent, config } = update?.request || {};
                const { delay } = config || {};
                if (update?.request && method === 'command') {
                  dispatch(pageRequest({
                    location: { pathname: value },
                    silent,
                    componentId: id,
                    onSuccess: onSuccessUpdates,
                    onError: onErrorUpdates,
                  }));
                } else if (update?.request && method === 'action') {
                  dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
                } else if (update?.request && method === 'open-url') {
                  handleOpenUrl(value, delay, silent, history);
                }
              });
            }
          };
          runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);

      } else if (regexpSuccessUpdates && result) {
          const styleUpdates = regexpSuccessUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
          const requestUpdates = regexpSuccessUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
          const functionUpdates = regexpSuccessUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
          const actionUpdates = regexpSuccessUpdates.filter((update): update is ActionChunk => update && 'action' in update);
          applyVariablesFirst(regexpSuccessUpdates);
          const runEffects = () => {
            if (styleUpdates.length) {
              dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
            }
            if (actionUpdates.length) {
              const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
              handleActionChunk(actionUpdates, filterLoadingState, onSuccessUpdates, onErrorUpdates);
            }
            if (requestUpdates.length) {
              requestUpdates.forEach((update) => {
                const { method, value, silent, config } = update?.request || {};
                const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
                const { delay } = config || {};
                if (update?.request && method === 'command') {
                  dispatch(pageRequest({
                    location: { pathname: value },
                    silent,
                    componentId: id,
                    onSuccess: onSuccessUpdates,
                    onError: onErrorUpdates,
                  }, filterLoadingState));
                } else if (update?.request && method === 'action') {
                  dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }, filterLoadingState));
                } else if (update?.request && method === 'open-url') {
                  handleOpenUrl(value, delay, silent, history);
                }
              });
            }
          };
          runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
      }
    }
    if (soc) {
      dispatch(submitFormRequest({ value: 'soc', delay, componentId: id }));
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (active_state === 'disabled' || active_state === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (focusStyles) {
      const styleUpdates = focusStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = focusStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = focusStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = focusStyles.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(focusStyles);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'command') {
              dispatch(pageRequest({
                location: { pathname: value },
                silent,
                componentId: id,
                onSuccess: onSuccessUpdates,
                onError: onErrorUpdates,
              }));
            } else if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (active_state === 'disabled' || active_state === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    dispatch(submitFormRequest({ block: true }));

    const newValue = e.target.value;

    setInternalValue(newValue);
    dispatch(formValuesRequest({ field: { id: realRef || id, value: newValue } }));

    if (blurStyles) {
      const styleUpdates = blurStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = blurStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = blurStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = blurStyles.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(blurStyles);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'command') {
              dispatch(pageRequest({
                location: { pathname: value },
                silent,
                componentId: id,
                onSuccess: onSuccessUpdates,
                onError: onErrorUpdates,
              }));
            } else if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  };

  const handleHover = (e: React.PointerEvent<HTMLTextAreaElement>) => {
    if (e.pointerType === 'touch') return; // TNBEWEB-1980: finger tap — don't apply hover
    if (hoverUpdates) {
      const styleUpdates = hoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = hoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = hoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = hoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(hoverUpdates);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);

    }

  }

  const handleUnhover = (e:  React.PointerEvent<HTMLTextAreaElement>) => {
    if (e.pointerType === 'touch') return; // TNBEWEB-1980: finger tap — don't apply unhover
    if (unhoverUpdates) {
      const styleUpdates = unhoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = unhoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = unhoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = unhoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(unhoverUpdates);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  }

  useEffect(() => {
    if (launchExecutedRef.current !== style?.visibility && launchUpdates && launchUpdates.length) {
      launchExecutedRef.current = style?.visibility; // Set the flag to true
      const requestUpdates = launchUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = launchUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = launchUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(launchUpdates);
      const runEffects = () => {
        if (actionUpdates.length) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              const newTimeout = setTimeout(() => {
                dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
              }, config?.delay * 1000 || 0);

              timeoutsRef.current.push(newTimeout);
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects);
    }

    return () => {
      timeoutsRef?.current?.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, [launchUpdates, style?.visibility]);

  // Memoize the filtered updates to avoid recalculating on every click
  const filteredUpdates = useMemo(() => {
    if (!outclickUpdates) return null;
    
    return {
      styleUpdates: outclickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update),
      requestUpdates: outclickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update),
      functionUpdates: outclickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update),
      actionUpdates: outclickUpdates.filter((update): update is ActionChunk => update && 'action' in update)
    };
  }, [outclickUpdates]);

  useEffect(() => {
    if (!outclickUpdates?.length) return undefined;
    if (style?.visibility === 'hidden') return undefined;

    const handleUnclick = (e: MouseEvent) => {
      if (active_state === 'disabled' || active_state === 'loading') return;
      
      if (
        outclickUpdates?.length &&
        inputRef.current && 
        !inputRef.current.contains(e.target as Node) &&
        !isClickInsidePortal(e.target as Node) &&
        filteredUpdates
      ) {
        const runEffects = () => {
          // Apply style updates
          if (filteredUpdates.styleUpdates.length) {
            dispatch(mergeStyleRequest(filteredUpdates.styleUpdates, parentIndex));
          }

          // Handle action updates
          if (filteredUpdates.actionUpdates.length) {
            handleActionChunk(filteredUpdates.actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
          }

          // Process request updates
          if (filteredUpdates.requestUpdates.length) {
            filteredUpdates.requestUpdates.forEach((update) => {
              const { method, value, silent, config } = update?.request || {};
              const { delay } = config || {};
              if (update?.request && method === 'action') {
                dispatch(submitFormRequest({
                  value,
                  silent,
                  delay,
                  componentId: id,
                  onSuccess: onSuccessUpdates,
                  onError: onErrorUpdates,
                }));
              } else if (update?.request && method === 'open-url') {
                handleOpenUrl(value, delay, silent, history);
              }
            });
          }
        };
        applyVariablesFirst(outclickUpdates);
        runFunctionsWithTiming(filteredUpdates.functionUpdates, internalFns, runEffects);
      }
    };
    
    document.addEventListener('mousedown', handleUnclick, true);
    return () => {
      document.removeEventListener('mousedown', handleUnclick, true);
    };
  }, [
    parentIndex, 
    active_state, 
    id, 
    stDraft?.states?.loading,
    outclickUpdates?.length,
    style?.visibility,
  ]);

  if (renderSceleton && style) {
    return <ContainerSkeleton
      style={style}
      baseScreenResolution={baseScreenResolution}
      skipStates={active_state === 'disabled' || active_state === 'loading'}
    />;
  }

  const TextareaEl = StyledTextarea;

  return (
    <TextareaEl
      ref={inputRef}
      placeholder={placeholder}
       data-id={realRef || id}
      id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
      $shape={style}
      $baseScreenResolution={baseScreenResolution}
      $skipStates={active_state === 'disabled' || active_state === 'loading'}
      $parentDisplay={parentStyle?.display}
      $parentFlexDirection={typeof parentStyle?.flex === 'object' ? parentStyle?.flex?.direction : undefined}
      data-component="input"
      className={combineClassNames(style.class)}
      value={internalValue}
      title={tooltip}

      autoFocus={autofocus}
      disabled={active_state === 'disabled'}
      onInput={handleInput}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onPointerEnter={hoverUpdates?.length ? handleHover : undefined}
      onPointerLeave={unhoverUpdates?.length ? handleUnhover : undefined}
      maxLength={regexp?.max_length}
    />
  );
};

const Email: React.FC<EmailInput> = ({ id, style, placeholder }) => <StyledInput placeholder={placeholder} id={id} type="email" $shape={style} data-component="input" className={combineClassNames(style.class)} />;

const Number: React.FC<NumberInput> = ({ id, style, placeholder }) => <StyledInput placeholder={placeholder} id={id} type="number" $shape={style} data-component="input" className={combineClassNames(style.class)} />;

const File: React.FC<FileInput> = ({ id, style, placeholder }) => <StyledInput placeholder={placeholder} id={id} type="file" $shape={style} data-component="input" className={combineClassNames(style.class)} />;

const Phone: React.FC<PhoneInput> = ({ id, style, placeholder }) => <StyledInput placeholder={placeholder} id={id} type="tel" $shape={style} data-component="input" className={combineClassNames(style.class)} />;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Global: React.FC<TextInput | TextAreaInput | PasswordInput | EmailInput | NumberInput | FileInput | PhoneInput | DateTimePickerInput> = (props) => {
  const { type } = props;

  switch (type) {
    case 'text':
    case 'password':
    case 'number':
      return <Input {...props} />;
    case 'textarea':
      return <TextArea {...props} />;
    // case 'password':
    //   return <Password {...props} />;
    case 'email':
      return <Email {...props} />;
    case 'number':
      return <Number {...props} />;
    case 'file':
      return <File {...props} />;
    case 'phone':
      return <Phone {...props} />;
    case 'daterangepicker':
      return <DateRangePicker {...props} />;
    default:
      return <Input {...props as TextInput} />;
  }
};

export default Global;
